import { useCallback, useEffect, useState } from "react";
import { Card, Col, Row, Typography, Button, Spin } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency } from "../../../utils/formatters";
import { fetchSystemConfig } from "../../../shared/lib/systemConfig";
import "../../../shared/styles/CurrencyTotals.css";

const { Title, Text } = Typography;

const emptyCash = {
  total_main: 0,
  total_secondary: 0,
};

const emptyDebt = {
  total_main: 0,
  total_secondary: 0,
};

const emptyBalances = {
  main_currency_code: "IQD",
  clients_debt_main: 0,
  our_debt_main: 0,
};

const emptyCollected = {
  main_currency_code: "IQD",
  secondary_currency_code: null,
  total_main: 0,
  total_secondary: 0,
  cash: { ...emptyCash },
  debt_adjustments: { ...emptyDebt },
};

function CompactBlock({ title, mainAmount, secondaryAmount, mainCode, secCode, accentClass }) {
  return (
    <Card size="small" variant="borderless" className={`currency-totals-compact-card ${accentClass}`}>
      <Text className="currency-totals-compact-heading">{title}</Text>
      <div className="currency-totals-compact-main">{formatCurrency(mainAmount, mainCode)}</div>
      {secCode ? (
        <div className="currency-totals-compact-secondary">{formatCurrency(secondaryAmount, secCode)}</div>
      ) : null}
    </Card>
  );
}

const CurrencyTotals = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [collected, setCollected] = useState(emptyCollected);
  const [balances, setBalances] = useState(emptyBalances);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await fetchSystemConfig();
      const res = await apiClient.get("/dashboard/summary");
      const c = res?.data?.currency_collected;
      const fb = res?.data?.financial_balances;
      if (fb && typeof fb === "object") {
        setBalances({
          ...emptyBalances,
          ...fb,
        });
      } else {
        setBalances(emptyBalances);
      }
      if (c && typeof c === "object") {
        setCollected({
          ...emptyCollected,
          ...c,
          cash: { ...emptyCash, ...(c.cash || {}) },
          debt_adjustments: { ...emptyDebt, ...(c.debt_adjustments || {}) },
        });
      } else {
        setCollected(emptyCollected);
      }
    } catch {
      setCollected(emptyCollected);
      setBalances(emptyBalances);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const mainCode = collected.main_currency_code || "IQD";
  const secCode = collected.secondary_currency_code || "";

  const cash = collected.cash || emptyCash;
  const debt = collected.debt_adjustments || emptyDebt;

  return (
    <div className="list-page currency-totals-page currency-totals-page--compact">
      <div className="currency-totals-header">
        <Title level={3} style={{ margin: 0 }}>
          {t("currencyTotalsPageTitle")}
        </Title>
        <Button type="default" icon={<ReloadOutlined />} onClick={load} loading={loading}>
          {t("currencyTotalsRefresh")}
        </Button>
      </div>

      <Spin spinning={loading}>
        <Row gutter={[16, 16]} className="currency-totals-grid">
          <Col xs={24} md={12} xl={8}>
            <CompactBlock
              accentClass="currency-totals-compact-card--net"
              title={t("currencyTotalsCompactAll")}
              mainAmount={collected.total_main}
              secondaryAmount={collected.total_secondary}
              mainCode={mainCode}
              secCode={secCode}
            />
          </Col>
          <Col xs={24} md={12} xl={8}>
            <CompactBlock
              accentClass="currency-totals-compact-card--cash"
              title={t("currencyTotalsCompactCash")}
              mainAmount={cash.total_main}
              secondaryAmount={cash.total_secondary}
              mainCode={mainCode}
              secCode={secCode}
            />
          </Col>
          <Col xs={24} md={12} xl={8}>
            <CompactBlock
              accentClass="currency-totals-compact-card--debt"
              title={t("currencyTotalsCompactDebt")}
              mainAmount={debt.total_main}
              secondaryAmount={debt.total_secondary}
              mainCode={mainCode}
              secCode={secCode}
            />
          </Col>
          <Col xs={24} md={12} xl={12}>
            <CompactBlock
              accentClass="currency-totals-compact-card--ar"
              title={t("currencyTotalsCompactClientsDebt")}
              mainAmount={balances.clients_debt_main}
              secondaryAmount={0}
              mainCode={balances.main_currency_code || mainCode}
              secCode=""
            />
          </Col>
          <Col xs={24} md={12} xl={12}>
            <CompactBlock
              accentClass="currency-totals-compact-card--ap"
              title={t("currencyTotalsCompactOurDebt")}
              mainAmount={balances.our_debt_main}
              secondaryAmount={0}
              mainCode={balances.main_currency_code || mainCode}
              secCode=""
            />
          </Col>
        </Row>

        <Text type="secondary" className="currency-totals-compact-footnote currency-totals-balances-hint">
          {t("currencyTotalsBalancesHint")}
        </Text>

        {!secCode ? (
          <Text type="secondary" className="currency-totals-compact-footnote">
            {t("currencyTotalsNoSecondaryHint")}
          </Text>
        ) : null}
      </Spin>
    </div>
  );
};

export default CurrencyTotals;
